// File scanner (ARCHITECTURE.md §6.6).
//
// Enumerates project source files, filtered by a global exclude list, and
// annotates each file with the DSL artifacts whose `path_patterns` match it.
// Files that match no artifact pattern are still returned (they may contain
// relevant code) but are also collected into `unmapped` so callers can report
// them. Path resolution (which file an import points to) is NOT done here —
// that is the Dependency Graph Builder's job (§6.7, Phase 5).
//
// Glob matching uses `tinyglobby` (the same lightweight engine tsup builds on):
// patterns and excludes are evaluated relative to `projectRoot`, so every path
// in the result is relative to the root, while `path` carries the absolute
// location for downstream IO.

import { join } from "node:path";

import { glob } from "tinyglobby";

/**
 * Default exclude patterns applied to every scan. Callers may override the
 * whole list via `ScanOptions.globalExclude`.
 */
export const DEFAULT_GLOBAL_EXCLUDE: readonly string[] = [
  "node_modules/**",
  ".git/**",
  "dist/**",
  "build/**",
  "coverage/**",
  "*.d.ts",
];

/** One scanned file, with the artifacts whose patterns matched it. */
export interface FileEntry {
  /** Absolute path on disk (for reading content). */
  path: string;
  /** Path relative to `projectRoot` (used as the graph node id). */
  relativePath: string;
  /** Ids of artifacts whose `path_patterns` matched this file (may be empty). */
  matchedArtifacts: string[];
}

export interface ScanOptions {
  /** Project root; all patterns and outputs are resolved relative to it. */
  projectRoot: string;
  /** artifactId → glob patterns (the DSL `path_patterns`). */
  artifactPatterns: Map<string, string[]>;
  /** Exclude patterns. Defaults to {@link DEFAULT_GLOBAL_EXCLUDE}. */
  globalExclude?: string[];
}

export interface ScanResult {
  /** Every scanned file, sorted by `relativePath`. */
  entries: FileEntry[];
  /** Relative paths of files matching no artifact pattern. */
  unmapped: string[];
}

/**
 * Enumerate files under `projectRoot`, assigning each to the artifacts whose
 * patterns match. Returns all files (mapped and unmapped) plus the list of
 * unmapped relative paths. Output is deterministic: entries are sorted by
 * `relativePath` and `matchedArtifacts` by artifact id.
 */
export async function scanFiles(options: ScanOptions): Promise<ScanResult> {
  const { projectRoot, artifactPatterns } = options;
  const ignore = options.globalExclude ?? [...DEFAULT_GLOBAL_EXCLUDE];

  // All candidate files, relative to the project root.
  // tinyglobby's `ignore` option is unreliable (v0.2.x); use negation patterns.
  const negations = ignore.map((p) => `!**/${p.replace(/^\*\*\//, "")}`);
  const allFiles = await glob(["**/*", ...negations], {
    cwd: projectRoot,
    onlyFiles: true,
  });

  // relativePath → set of artifact ids that matched it.
  const matched = new Map<string, Set<string>>();
  for (const [artifactId, patterns] of artifactPatterns) {
    if (patterns.length === 0) continue;
    const files = await glob([...patterns, ...negations], {
      cwd: projectRoot,
      onlyFiles: true,
    });
    for (const rel of files) {
      let set = matched.get(rel);
      if (!set) {
        set = new Set<string>();
        matched.set(rel, set);
      }
      set.add(artifactId);
    }
  }

  const entries: FileEntry[] = allFiles.map((rel) => ({
    path: join(projectRoot, rel),
    relativePath: rel,
    matchedArtifacts: [...(matched.get(rel) ?? [])].sort(),
  }));
  entries.sort((a, b) =>
    a.relativePath < b.relativePath ? -1 : a.relativePath > b.relativePath ? 1 : 0,
  );

  const unmapped = entries
    .filter((entry) => entry.matchedArtifacts.length === 0)
    .map((entry) => entry.relativePath);

  return { entries, unmapped };
}
